import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import UpButton from '../../components/Common/UpButton';
import AppPage from './AppPage';
import {
  createSignSession,
  getSignupErrorMessage,
  getSignupRequestId,
} from '../../api/signup';

// https only — an http iframe inside an https page is silently blocked
// (mixed content) and would render as a blank frame.
function getSafeEmbedUrl(session) {
  try {
    const url = new URL(session?.embed_url);
    return url.protocol === 'https:' ? url.href : null;
  } catch (e) {
    return null;
  }
}

function isPandaDocOrigin(origin) {
  return (
    typeof origin === 'string' &&
    (origin === 'https://app.pandadoc.com' || origin.endsWith('.pandadoc.com'))
  );
}

// The Order Form signs EMBEDDED in this page. The previous new-tab flow
// stranded the merchant on PandaDoc's page after signing with no way back
// (Archit's flow review, Aug 6) — the iframe posts
// session_view.document.completed to this window instead, and the page
// advances in place. The 3s status poll (SignupWizard) stays as fallback.
// The card widens on this step and the frame can go full screen — a legal
// document has to be READABLE before it's signed.
function SignView({ token, state }) {
  const queryClient = useQueryClient();
  const [embedError, setEmbedError] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // Reload must remount the iframe even when the server re-uses the same
  // live session (unchanged embedUrl) — the nonce forces a fresh node.
  const [reloadNonce, setReloadNonce] = useState(0);

  // The server re-uses live sessions and re-mints stale ones, so refetching
  // is always safe — refetchOnWindowFocus re-mints for the merchant who
  // opened the link and came back hours after the session expired.
  const {
    data: session,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['signup-sign-session', token],
    queryFn: () => createSignSession(token),
    retry: 1,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    function onMessage(event) {
      if (!isPandaDocOrigin(event.origin)) {
        return;
      }

      if (event.data?.type === 'session_view.document.completed') {
        // Signed — leave full screen so the merchant sees the page advance,
        // and make the status poll fire now instead of in ≤3s.
        setExpanded(false);
        queryClient.invalidateQueries({ queryKey: ['signup-status', token] });
      }

      if (event.data?.type === 'session_view.document.exception') {
        setExpanded(false);
        setEmbedError(true);
      }
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [queryClient, token]);

  // Best-effort: Escape only reaches this window while focus is OUTSIDE the
  // cross-origin iframe, so the button never advertises it (CodeRabbit) —
  // the visible exit is the button itself.
  useEffect(() => {
    if (!expanded) {
      return undefined;
    }

    function onKeyDown(event) {
      if (event.key === 'Escape') {
        setExpanded(false);
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [expanded]);

  function reloadSigningForm() {
    setEmbedError(false);
    setReloadNonce(value => value + 1);
    refetch();
  }

  const embedUrl = getSafeEmbedUrl(session);
  const showBadSession = !error && session && !embedUrl;

  return (
    <AppPage step={state?.step} loading={isLoading} wide>
      <h1 className="signup-title">Sign your order form</h1>
      <p className="signup-sub">
        {state?.business || 'Your brand'}&apos;s Order Form is ready for
        signature.
      </p>
      {/* View-only from here — the details are on the legal document
          (owner's rule: see, don't edit, once the form exists). */}
      <details className="text-sm text-up-clr-dark-grey">
        <summary className="cursor-pointer text-up-clr-light-grey">
          Your details (view only)
        </summary>
        <div className="mt-2">
          <div>Brand: {state?.business}</div>
          <div>Legal entity: {state?.identity?.legal_entity}</div>
          <div>
            Billing contact: {state?.identity?.billing_contact_name} ·{' '}
            {state?.identity?.billing_contact_email}
          </div>
          {state?.billing_address && (
            <div>
              Address: {state.billing_address.street},{' '}
              {state.billing_address.city}, {state.billing_address.state}{' '}
              {state.billing_address.zip}
            </div>
          )}
        </div>
      </details>
      {(error || showBadSession || embedError) && (
        <div className="text-sm text-up-clr-semantic-red break-words mt-6">
          {error
            ? getSignupErrorMessage(
                error,
                'We could not prepare your signing page. Please try again.',
              )
            : 'The signing form ran into a problem. Please reload it.'}
          {getSignupRequestId(error) && (
            <span className="block text-xs text-up-clr-light-grey mt-1">
              Reference: {getSignupRequestId(error)}
            </span>
          )}
          <UpButton
            variant="link"
            wrapperClass="w-max mt-2"
            onClick={reloadSigningForm}>
            Reload signing form
          </UpButton>
        </div>
      )}
      {embedUrl && !embedError && (
        <>
          <div className={cx('signup-sign-frame-wrap mt-6', { expanded })}>
            <div className="sign-frame-bar">
              <span className="frame-bar-title">
                Order Form — {state?.business || 'your brand'}
              </span>
              <button type="button" onClick={() => setExpanded(!expanded)}>
                {expanded ? 'Exit full screen' : '⛶ Read full screen'}
              </button>
            </div>
            {/* sandbox: signing needs scripts+forms+same-origin (PandaDoc's
                documented embed set); popups because the Order Form carries
                clickable ToS links that open in a new tab, and the post-sign
                screen offers the signed-copy download. Top-level navigation
                stays blocked — that's the hijack vector. */}
            <iframe
              key={`${embedUrl}-${reloadNonce}`}
              title="UrbanPiper — Order Form"
              src={embedUrl}
              className="signup-sign-frame"
              allow="clipboard-write"
              sandbox="allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-downloads"
            />
          </div>
          <p className="text-xs text-up-clr-light-grey text-center mt-3">
            Review and sign above — this page moves ahead automatically once
            you&rsquo;ve signed.{' '}
            <button
              type="button"
              className="underline"
              onClick={reloadSigningForm}>
              Reload the form
            </button>{' '}
            if it looks stuck.
          </p>
        </>
      )}
    </AppPage>
  );
}

export default SignView;
